import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Table, Alert, Modal } from 'react-bootstrap';
import { Plus, Upload, Check, X } from 'lucide-react';
import { creditLeaves, bulkCreditLeaves } from '../../components/api/LeaveApi';
import { getEmployeesWithLeaveApplicable } from '../../components/api/EmpMasterApi';

const LeaveCreditAllocation = () => {
     const [showModal, setShowModal] = useState(false);
     const [employees, setEmployees] = useState([]);
     const [formData, setFormData] = useState({
          emp_code: '',
          leave_type: 'PL',
          total_leaves: '',
          year_start: '',
          year_end: ''
     });
     const [loading, setLoading] = useState(false);
     const [message, setMessage] = useState({ type: '', text: '' });
     const [bulkMode, setBulkMode] = useState(false);
     const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

     useEffect(() => {
          const handleResize = () => setIsMobile(window.innerWidth < 768);
          window.addEventListener('resize', handleResize);
          return () => window.removeEventListener('resize', handleResize);
     }, []);

     useEffect(() => {
          fetchEmployees();
          setDefaultFinancialYear();
     }, []);

     const fetchEmployees = async () => {
          try {
               const response = await getEmployeesWithLeaveApplicable();
               console.log('API Response:', response);
               setEmployees(response.data || []);
          } catch (error) {
               console.error('Error fetching employees:', error);
               setEmployees([]);
          }
     };

     const setDefaultFinancialYear = () => {
          const today = new Date();
          const currentYear = today.getFullYear();
          const currentMonth = today.getMonth();

          const fyStart = currentMonth >= 3 ? currentYear : currentYear - 1;
          const fyEnd = fyStart + 1;

          setFormData(prev => ({
               ...prev,
               year_start: `${fyStart}-04-01`,
               year_end: `${fyEnd}-03-31`
          }));
     };

     const handleSubmit = async (e) => {
          e.preventDefault();
          setLoading(true);
          setMessage({ type: '', text: '' });

          try {
               await creditLeaves(formData);
               setMessage({ type: 'success', text: 'Leaves credited successfully!' });
               setFormData({
                    ...formData,
                    emp_code: '',
                    total_leaves: ''
               });
               setTimeout(() => setShowModal(false), 1500);
          } catch (error) {
               setMessage({ type: 'danger', text: error.response?.data?.message || 'Failed to credit leaves' });
          } finally {
               setLoading(false);
          }
     };

     const handleBulkCredit = async () => {
          setLoading(true);
          setMessage({ type: '', text: '' });

          const bulkData = {
               leave_type: formData.leave_type,
               total_leaves: formData.total_leaves,
               year_start: formData.year_start,
               year_end: formData.year_end,
               employee_codes: employees
                    .filter(emp => emp.isLeaveApplicable === 'Y')
                    .map(emp => emp.employee_code)
          };

          try {
               const result = await bulkCreditLeaves(bulkData);
               setMessage({
                    type: 'success',
                    text: `Successfully credited leaves to ${result.success_count} employees`
               });
          } catch (error) {
               setMessage({ type: 'danger', text: 'Failed to credit leaves in bulk' });
          } finally {
               setLoading(false);
          }
     };

     return (
          <div className="leave-credit-section" style={{ padding: isMobile ? '12px' : '24px' }}>
               {/* Header */}
               <Row className="mb-3">
                    <Col xs={12} md={6} className="mb-2 mb-md-0">
                         <h4 style={{ fontSize: isMobile ? '18px' : '24px' }}>Credit Leave Allocation</h4>
                    </Col>
                    <Col xs={12} md={6} className={isMobile ? '' : 'text-end'}>
                         <div className={`d-flex gap-2 ${isMobile ? 'flex-column' : 'flex-row justify-content-end'}`}>
                              <Button
                                   variant="primary"
                                   size="sm"
                                   onClick={() => setShowModal(true)}
                                   className={isMobile ? 'w-100' : ''}
                                   style={{ fontSize: isMobile ? '13px' : '14px' }}
                              >
                                   <Plus size={16} className="me-1" />
                                   Credit Individual
                              </Button>
                              <Button
                                   variant="success"
                                   size="sm"
                                   onClick={() => setBulkMode(!bulkMode)}
                                   className={isMobile ? 'w-100' : ''}
                                   style={{ fontSize: isMobile ? '13px' : '14px' }}
                              >
                                   <Upload size={16} className="me-1" />
                                   Bulk Credit
                              </Button>
                         </div>
                    </Col>
               </Row>

               {/* Alert Message */}
               {message.text && (
                    <Alert
                         variant={message.type}
                         onClose={() => setMessage({ type: '', text: '' })}
                         dismissible
                         style={{ fontSize: isMobile ? '13px' : '14px' }}
                    >
                         {message.text}
                    </Alert>
               )}

               {/* Bulk Credit Section */}
               {bulkMode && (
                    <Card className="mb-4" style={{ borderRadius: isMobile ? '8px' : '12px' }}>
                         <Card.Header className="bg-light" style={{ padding: isMobile ? '10px 12px' : '12px 16px' }}>
                              <h5 className="mb-0" style={{ fontSize: isMobile ? '14px' : '16px' }}>Bulk Leave Credit</h5>
                         </Card.Header>
                         <Card.Body style={{ padding: isMobile ? '12px' : '16px' }}>
                              <Form>
                                   <Row className="g-2">
                                        <Col xs={12} md={3}>
                                             <Form.Group className="mb-2">
                                                  <Form.Label className="small fw-semibold mb-1" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                       Leave Type
                                                  </Form.Label>
                                                  <Form.Select
                                                       value={formData.leave_type}
                                                       onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })}
                                                       size="sm"
                                                       style={{ fontSize: isMobile ? '13px' : '14px' }}
                                                  >
                                                       <option value="PL">Privilege Leave (PL)</option>
                                                       <option value="CL">Casual Leave (CL)</option>
                                                       <option value="SL">Sick Leave (SL)</option>
                                                  </Form.Select>
                                             </Form.Group>
                                        </Col>
                                        <Col xs={12} md={2}>
                                             <Form.Group className="mb-2">
                                                  <Form.Label className="small fw-semibold mb-1" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                       Total Leaves
                                                  </Form.Label>
                                                  <Form.Control
                                                       type="number"
                                                       value={formData.total_leaves}
                                                       onChange={(e) => setFormData({ ...formData, total_leaves: e.target.value })}
                                                       placeholder="Days"
                                                       size="sm"
                                                       style={{ fontSize: isMobile ? '13px' : '14px' }}
                                                  />
                                             </Form.Group>
                                        </Col>
                                        <Col xs={12} md={3}>
                                             <Form.Group className="mb-2">
                                                  <Form.Label className="small fw-semibold mb-1" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                       Period Start
                                                  </Form.Label>
                                                  <Form.Control
                                                       type="date"
                                                       value={formData.year_start}
                                                       onChange={(e) => setFormData({ ...formData, year_start: e.target.value })}
                                                       size="sm"
                                                       style={{ fontSize: isMobile ? '13px' : '14px' }}
                                                  />
                                             </Form.Group>
                                        </Col>
                                        <Col xs={12} md={3}>
                                             <Form.Group className="mb-2">
                                                  <Form.Label className="small fw-semibold mb-1" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                       Period End
                                                  </Form.Label>
                                                  <Form.Control
                                                       type="date"
                                                       value={formData.year_end}
                                                       onChange={(e) => setFormData({ ...formData, year_end: e.target.value })}
                                                       size="sm"
                                                       style={{ fontSize: isMobile ? '13px' : '14px' }}
                                                  />
                                             </Form.Group>
                                        </Col>
                                        <Col xs={12} md={1} className={isMobile ? '' : 'd-flex align-items-end'}>
                                             <Button
                                                  variant="success"
                                                  size="sm"
                                                  onClick={handleBulkCredit}
                                                  disabled={loading || !formData.total_leaves}
                                                  className={`${isMobile ? 'w-100 mb-2' : 'mb-2'}`}
                                                  style={{
                                                       fontSize: isMobile ? '13px' : '14px',
                                                       minHeight: '36px'
                                                  }}
                                             >
                                                  {loading ? (
                                                       <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                  ) : (
                                                       <>
                                                            <Check size={16} className={isMobile ? 'me-1' : ''} />
                                                            {isMobile && 'Apply'}
                                                       </>
                                                  )}
                                             </Button>
                                        </Col>
                                   </Row>
                              </Form>
                              <Alert variant="info" className="mb-0 mt-3" style={{ fontSize: isMobile ? '11px' : '12px', padding: isMobile ? '8px 12px' : '12px' }}>
                                   This will credit leaves to all employees where <code>isLeaveApplicable = 'Y'</code>
                              </Alert>
                         </Card.Body>
                    </Card>
               )}

               {/* Individual Credit Modal */}
               <Modal
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    centered
                    fullscreen={isMobile ? 'sm-down' : false}
                    className=' '
               >
                    <Modal.Header closeButton className="border-0 pb-0">
                         <Modal.Title style={{ fontSize: isMobile ? '16px' : '18px' }}>
                              Credit Leaves to Employee
                         </Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleSubmit}>
                         <Modal.Body className='m-4' style={{ padding: isMobile ? '12px' : '16px' }}>
                              <Row className="g-2">
                                   <Col xs={12} md={6}>
                                        <Form.Group className="mb-3">
                                             <Form.Label className="small fw-semibold mb-1" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                  Employee
                                             </Form.Label>
                                             <Form.Select
                                                  value={formData.emp_code}
                                                  onChange={(e) => setFormData({ ...formData, emp_code: e.target.value })}
                                                  required
                                                  size="sm"
                                                  style={{ fontSize: isMobile ? '13px' : '14px' }}
                                             >
                                                  <option value="">Select Employee</option>
                                                  {employees
                                                       .filter(emp => emp.isLeaveApplicable === 'Y')
                                                       .map(emp => (
                                                            <option key={emp.employee_code} value={emp.employee_code}>
                                                                 {emp.employee_code} - {emp.employee_name}
                                                            </option>
                                                       ))}
                                             </Form.Select>
                                        </Form.Group>
                                   </Col>
                                   <Col xs={12} md={6}>
                                        <Form.Group className="mb-3">
                                             <Form.Label className="small fw-semibold mb-1" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                  Leave Type
                                             </Form.Label>
                                             <Form.Select
                                                  value={formData.leave_type}
                                                  onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })}
                                                  required
                                                  size="sm"
                                                  style={{ fontSize: isMobile ? '13px' : '14px' }}
                                             >
                                                  <option value="PL">Privilege Leave (PL)</option>
                                                  <option value="CL">Casual Leave (CL)</option>
                                                  <option value="SL">Sick Leave (SL)</option>
                                             </Form.Select>
                                        </Form.Group>
                                   </Col>

                                   <Col xs={12} md={4}>
                                        <Form.Group className="mb-3">
                                             <Form.Label className="small fw-semibold mb-1" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                  Total Leaves
                                             </Form.Label>
                                             <Form.Control
                                                  type="number"
                                                  step="0.5"
                                                  value={formData.total_leaves}
                                                  onChange={(e) => setFormData({ ...formData, total_leaves: e.target.value })}
                                                  placeholder="Days"
                                                  required
                                                  size="sm"
                                                  style={{ fontSize: isMobile ? '13px' : '14px' }}
                                             />
                                        </Form.Group>
                                   </Col>
                                   <Col xs={12} md={4}>
                                        <Form.Group className="mb-3">
                                             <Form.Label className="small fw-semibold mb-1" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                  Period Start
                                             </Form.Label>
                                             <Form.Control
                                                  type="date"
                                                  value={formData.year_start}
                                                  onChange={(e) => setFormData({ ...formData, year_start: e.target.value })}
                                                  required
                                                  size="sm"
                                                  style={{ fontSize: isMobile ? '13px' : '14px' }}
                                             />
                                        </Form.Group>
                                   </Col>
                                   <Col xs={12} md={4}>
                                        <Form.Group className="mb-3">
                                             <Form.Label className="small fw-semibold mb-1" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                  Period End
                                             </Form.Label>
                                             <Form.Control
                                                  type="date"
                                                  value={formData.year_end}
                                                  onChange={(e) => setFormData({ ...formData, year_end: e.target.value })}
                                                  required
                                                  size="sm"
                                                  style={{ fontSize: isMobile ? '13px' : '14px' }}
                                             />
                                        </Form.Group>
                                   </Col>
                              </Row>
                         </Modal.Body>
                         <Modal.Footer className="border-0 pt-0" style={{ padding: isMobile ? '12px' : '16px' }}>
                              <div className={`d-flex gap-2 ${isMobile ? 'w-100 flex-column-reverse' : ''}`}>
                                   <Button
                                        variant="light"
                                        onClick={() => setShowModal(false)}
                                        size="sm"
                                        className={isMobile ? 'w-100' : ''}
                                        style={{ fontSize: isMobile ? '13px' : '14px' }}
                                   >
                                        Cancel
                                   </Button>
                                   <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={loading}
                                        size="sm"
                                        className={isMobile ? 'w-100' : ''}
                                        style={{ fontSize: isMobile ? '13px' : '14px' }}
                                   >
                                        {loading ? 'Processing...' : 'Credit Leaves'}
                                   </Button>
                              </div>
                         </Modal.Footer>
                    </Form>
               </Modal>
          </div>
     );
};

export default LeaveCreditAllocation;